"use client";

import {
	Accordion,
	Button,
	Heading,
	RichText,
	Text
} from "@pamperedchef/design-system";
import type { CatalogSpecification } from "@/domain/catalog";
import * as styles from "./AboutTheProduct.module.scss";

/**
 * Resolved Use & Care guide content. The PDP route resolves the
 * `use_and_care_block.use_and_care_ref` (a `[{referenceId, contentType}]`
 * array) via `content-service /retrieve` before passing it down — the
 * composite itself stays purely presentational.
 */
export interface UseAndCareGuide {
	title: string;
	eyebrowText?: string;
	/** HTML string (RTE) — rendered via DS `RichText` on the standalone page. */
	bodyHtml?: string;
}

/**
 * One Dimensions / Features / Care & Safety accordion body sourced from the
 * CMS `about_product` content block. `html` is a ready-to-render RTE HTML
 * string (`content-service` serializes the Contentstack JSON RTE upstream);
 * `defaultOpen` mirrors the author's per-accordion default-open flag.
 */
export interface AboutProductSection {
	html?: string;
	defaultOpen?: boolean;
}

/**
 * CMS-authored content for the accordions, normalized from the
 * `about_product` block by `ProductDetailTemplate`. Dimensions and Features
 * render this HTML in place of the spec-derived bullets; Care & Safety
 * renders its notes below the spec-derived Materials / Country of origin.
 */
export interface AboutProductContent {
	dimensions?: AboutProductSection;
	features?: AboutProductSection;
	careSafety?: AboutProductSection;
}

export interface AboutTheProductProps {
	specification?: CatalogSpecification;
	/** CMS-authored accordion content from the `about_product` block. */
	aboutProduct?: AboutProductContent;
	consultantSellingTips?: string;
	useAndCareGuide?: UseAndCareGuide;
	useAndCareHref?: string;
	/**
	 * Invoked synchronously when "View Use & Care Guide" is pressed, before
	 * the guide tab is opened — analytics must observe the click first.
	 */
	onUseAndCareClick?: () => void;
	className?: string;
}

/**
 * "About the Product" — the row of accordions below the PDP hero
 * (Dimensions, Features, Care & Safety, For Consultants) plus the "View
 * Use & Care Guide" affordance. Each accordion is omitted entirely when its
 * underlying source is empty; the whole composite collapses to `null` when
 * nothing at all is provided.
 *
 * Dimensions and Features render the CMS `about_product` RTE HTML (via DS
 * `RichText`). Care & Safety leads with the spec-derived Materials / Country
 * of origin bullets, then appends the authored care notes below them. The DS
 * `Accordion` owns the chevron + divider chrome; per-accordion default-open
 * state comes from the CMS `default_open` flags.
 */
export function AboutTheProduct({
	specification,
	aboutProduct,
	consultantSellingTips,
	useAndCareGuide,
	useAndCareHref,
	onUseAndCareClick,
	className
}: AboutTheProductProps): JSX.Element | null {
	const built = buildSections(
		specification,
		aboutProduct,
		consultantSellingTips
	);
	const sections = built.map(({ title, content }) => ({ title, content }));
	const initialOpen = built
		.map((section, index) => (section.defaultOpen ? index : -1))
		.filter((index) => index >= 0);
	const hasAnySection = sections.length > 0;
	const hasGuide = Boolean(useAndCareGuide?.title) && Boolean(useAndCareHref);

	if (!hasAnySection && !hasGuide) return null;

	return (
		<section
			className={[styles["about"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="AboutTheProduct"
			aria-labelledby="about-the-product-heading"
		>
			<Heading
				tag="h2"
				id="about-the-product-heading"
				className={styles["about__heading"]}
			>
				About the Product
			</Heading>

			{hasAnySection ? (
				<Accordion
					sections={sections}
					initialOpen={initialOpen}
					inline
				/>
			) : null}

			{hasGuide && useAndCareHref ? (
				<Button
					kind="secondary"
					size="lg"
					leadingIcon={{ name: "book" }}
					onPress={() => {
						onUseAndCareClick?.();
						openInNewTab(useAndCareHref);
					}}
					className={styles["about__guide-trigger"]}
				>
					View Use &amp; Care Guide
				</Button>
			) : null}
		</section>
	);
}

// react-aria onPress can popup-block window.open; a synthetic anchor
// click opens the guide tab reliably.
function openInNewTab(href: string): void {
	const link = document.createElement("a");
	link.href = href;
	link.target = "_blank";
	link.rel = "noopener noreferrer";
	document.body.appendChild(link);
	link.click();
	link.remove();
}

interface BuiltSection {
	title: string;
	content: React.ReactNode;
	defaultOpen?: boolean;
}

/**
 * Builds the array passed to DS `Accordion`. Each section returns `null`
 * when its source content is empty — those entries are filtered out so DS
 * never renders an empty title.
 */
function buildSections(
	spec: CatalogSpecification | undefined,
	aboutProduct: AboutProductContent | undefined,
	consultantSellingTips: string | undefined
): BuiltSection[] {
	return [
		renderDimensions(aboutProduct),
		renderFeatures(aboutProduct),
		renderCareAndSafety(spec, aboutProduct),
		renderForConsultants(consultantSellingTips)
	].filter((section): section is BuiltSection => section !== null);
}

/** Renders an array of strings as the DS-styled bullet list shown in Care & Safety. */
function bulletList(items: string[]): JSX.Element {
	return (
		<ul className={styles["about__bullets"]}>
			{items.map((item, i) => (
				<li key={`${item}-${i}`} className={styles["about__bullet"]}>
					<Text tag="span" styledAs="mdr">
						{item}
					</Text>
				</li>
			))}
		</ul>
	);
}

function renderDimensions(
	aboutProduct: AboutProductContent | undefined
): BuiltSection | null {
	const section = aboutProduct?.dimensions;
	if (!section?.html) return null;
	return {
		title: "Dimensions",
		content: <RichText content={section.html} />,
		defaultOpen: section.defaultOpen
	};
}

function renderFeatures(
	aboutProduct: AboutProductContent | undefined
): BuiltSection | null {
	const section = aboutProduct?.features;
	if (!section?.html) return null;
	return {
		title: "Features",
		content: <RichText content={section.html} />,
		defaultOpen: section.defaultOpen
	};
}

function renderCareAndSafety(
	spec: CatalogSpecification | undefined,
	aboutProduct: AboutProductContent | undefined
): BuiltSection | null {
	// Compliance flags surface as chips on the FeatureHighlight block
	// (see complianceFlagsToChips). This section keeps materials + origin,
	// then appends the CMS-authored care notes below them.
	const items: string[] = [];
	const materials = spec?.materials ?? [];
	const country = spec?.countryOfOrigin;
	if (materials.length > 0) items.push(`Materials: ${materials.join(", ")}`);
	if (country) items.push(`Country of origin: ${country}`);

	const careNotes = aboutProduct?.careSafety;
	const careHtml = careNotes?.html;
	if (items.length === 0 && !careHtml) return null;

	return {
		title: "Care & Safety",
		content: (
			<div className={styles["about__stack"]}>
				{items.length > 0 ? bulletList(items) : null}
				{careHtml ? (
					<div className={styles["about__care-notes"]}>
						<RichText content={careHtml} />
					</div>
				) : null}
			</div>
		),
		defaultOpen: careNotes?.defaultOpen
	};
}

function renderForConsultants(
	sellingTipsHtml: string | undefined
): BuiltSection | null {
	if (!sellingTipsHtml) return null;
	return {
		title: "For Consultants",
		content: <RichText content={sellingTipsHtml} />
	};
}
